const EventEmitter = require('eventemitter3');
const { GAME_MODES } = require('../constants/Game');
import getUnitState from '../isomorphic-helpers/getUnitState';
const { setLogger, logError } = require('./logger');
const BattleState = require('./BattleState');
import AbilityExecutor from './AbilityExecutor';
const AIHandler = require('./ai/AIHandler');
const BattleCalc = require('./BattleCalc');
const IntervalTimer = require('./IntervalTimer');
const AStarSearch = require('./ai/AStarSearch/AStarSearch');

const IMMEDIATE_EXECUTION_ABILITIES = ['move', 'adrenaline', 'mystic_focus'];

export default function BattleEngine(battle_data: any, logger: any) {
  const {
    started_at,
    battleState,
    blackPlayerId,
    fightLive,
    game_mode,
    game_submode,
    isRecovery,
    whitePlayerId,
  } = battle_data;

  // the battle engine can run from the browser (caverns) or the server (arena),
  // but the logging module is different for each & has to be injected
  setLogger(logger);

  let _done = false;
  let _party_defeated = false;

  let battle_id = (this.battle_id = battle_data.battle_id);
  this.whitePlayerId = whitePlayerId;
  this.blackPlayerId = blackPlayerId;
  this.fightLive = fightLive;
  const isCaverns = battle_id.includes('caverns');
  this.current_caverns_level = isCaverns ? parseInt(battle_id.split('-')[1]) : NaN;
  this.game_submode = game_submode;

  this.setBattleId = (val) => {
    battle_id = this.battle_id = val;
  };
  this.setPartyDefeated = () => {
    _party_defeated = true;
  };
  this.isPartyDefeated = () => {
    return _party_defeated;
  };
  this.eventEmitter = new EventEmitter();
  var _aStarSearch = new AStarSearch();

  const emitToAllSockets = (this.emitToAllSockets = (message, data) => {
    try {
      if (!this.eventEmitter) {
        return;
      }

      if (isCaverns) {
        this.eventEmitter.emit(message, data);
      } else {
        this.eventEmitter.emit('publish_to_battle', {
          battle_id,
          message,
          data,
        });
      }
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'emitToAllSockets',
        message,
        data,
      });
    }
  });

  const emitToPlayer = (this.emitToPlayer = (message, data) => {
    try {
      if (!this.eventEmitter) {
        return;
      }

      this.eventEmitter.emit('publish_to_player', {
        playerId: data.playerId,
        message,
        data,
      });
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'emitToPlayer',
        message,
        data,
      });
    }
  });

  // initialize battle state
  const _battleState = (this.battleState = new BattleState(battleState, isRecovery, isCaverns));
  _battleState.eventEmitter.on(BattleState.AP_FULL, onFullAP);
  _battleState.eventEmitter.on(BattleState.BATTLE_EVENT, onBattleEvent);
  _battleState.eventEmitter.on(BattleState.UNIT_CONDITION, onUnitCondition);
  _battleState.eventEmitter.on(BattleState.UNIT_CONDITION_EXPIRED, onUnitConditionExpired);
  // init AI Handler and ability executer
  const _abilityExecutor = new AbilityExecutor(_battleState);
  const _aiHandler = (this.aiHandler = new AIHandler(_battleState));
  for (const unit of Object.values(_battleState.allUnits || {})) {
    _aiHandler.addUnitDecisions(unit);
  }


  let blacklistedAbilities = new Set();
  let _last_orders_request_at = 0;
  let _num_orders_requests_this_turn = 0;
  let _requestOrdersInterval: NodeJS.Timeout;
  let _allUnitsToOrder = [];
  let _unitToOrder = null;
  let _lastRequestedIdFromClient = null;

  this.init = () => {
    clearInterval(_requestOrdersInterval);
    _requestOrdersInterval = setInterval(async () => {
      try {
        if (_done || _party_defeated) {
          clearInterval(_requestOrdersInterval);
          return;
        }

        if (_abilityExecuting) {
          return;
        }

        const now = Date.now();

        if (
          _unitToOrder &&
          _unitToOrder.uid === _lastRequestedIdFromClient &&
          now - _last_orders_request_at > 1500 &&
          ((_unitToOrder.team === 'white' && game_mode !== GAME_MODES.GAME_MODE_pvpAuto) || fightLive)
        ) {
          // repeat the orders request, in case of a reconnection (or something else going wrong on the client?)
          emitToPlayer('requestOrders', {
            playerId: _unitToOrder.team === 'white' ? whitePlayerId : blackPlayerId,
            heroId: _unitToOrder.uid,
          });
          _last_orders_request_at = now;
          return;
        }

        if (!_unitToOrder && _allUnitsToOrder.length === 0) {
          // no units awaiting orders
          this.resume();
          return;
        }

        if (_unitToOrder) {
          if (!_unitToOrder.inPlay) {
            delete _battleState.allPieces[_unitToOrder.uid];
            delete _battleState.allUnits[_unitToOrder.uid];
            _unitToOrder = null;
            return;
          }

          if (BattleCalc.getCondition(_unitToOrder, 'stunned')) {
            _unitToOrder = null;
            return;
          }
        }

        if (_unitToOrder && _unitToOrder.uid !== _lastRequestedIdFromClient) {
          if (fightLive) {
            emitToAllSockets('turnTimerInit', {
              playerId: _unitToOrder.team === 'white' ? whitePlayerId : blackPlayerId,
              unitToOrderId: _unitToOrder.uid,
            });
            clearTimeout(_inactivityTimeout);
            _inactivityTimeout = setTimeout(handleInactivityTimeout, 60 * 1000);
          }

          // it's still someone's turn
          if (
            isCaverns ||
            game_mode === GAME_MODES.GAME_MODE_pvpAuto ||
            !_unitToOrder.hero ||
            _unitToOrder.poppet ||
            (!fightLive && _unitToOrder.team === 'black') ||
            BattleCalc.getCondition(_unitToOrder, 'enraged') ||
            BattleCalc.getCondition(_unitToOrder, 'scared')
          ) {
            // catch-all for breaking infinite AI loops
            if (++_num_orders_requests_this_turn > 10) {
              passTurn(_unitToOrder);
              return;
            }

            emitToAllSockets('aiTurn', {
              aiUnitId: _unitToOrder.uid,
              allUnits: _battleState.allUnits,
            });

            // action will be selected by AI
            const action_data = _aiHandler.getAction(_unitToOrder.uid, blacklistedAbilities);

            if (action_data === 'wait') {
              passTurn(_unitToOrder);
            } else {
              attemptExecuteAbility(action_data);
            }
            return;
          } else {
            // we have a player-controlled hero
            emitToPlayer('requestOrders', {
              playerId: _unitToOrder.team === 'white' ? whitePlayerId : blackPlayerId,
              heroId: _unitToOrder.uid,
            });

            _lastRequestedIdFromClient = _unitToOrder.uid;
            _last_orders_request_at = now;

            this.pause();
            emitToAllSockets('paused', { allUnits: _battleState.allUnits });
          }
        } else if (_unitToOrder && _unitToOrder.uid === _lastRequestedIdFromClient) {
          // repeat orders request indefinitely
          const unitToOrderPlayerId = _unitToOrder.team === 'white' ? whitePlayerId : blackPlayerId;
          if (unitToOrderPlayerId) {
            emitToPlayer('requestOrders', {
              playerId: unitToOrderPlayerId,
              heroId: _unitToOrder.uid,
            });
          }
          return;
        } else {
          // no _unitToOrder
          _unitToOrder = _allUnitsToOrder.shift();
          this.pause();
          return;
        }
      } catch (err) {
        logError(err, {
          module: 'BattleEngine',
          func: 'requestOrdersInterval',
        });
      }
    }, 200);

    setTimeout(_abilityExecutor.executeAngelMasks, 100);

    _battleState.init();
  };

  const _angel_mask_interval = setInterval(_abilityExecutor.executeAngelMasks, 1500);

  // caverns enemy spawn routine
  let _enemySpawnInterval;
  const spawnEnemy = () => {
    if (!_battleState || _done) {
      return;
    }

    if (_enemySpawnInterval) {
      _enemySpawnInterval.pause();
      _enemySpawnInterval.dispose();
    }
    _enemySpawnInterval = new IntervalTimer(spawnEnemy, 2500 + Math.random() * 10000);

    if (!_paused) {
      this.eventEmitter.emit('spawn_new_enemy');
    }
  };

  // caverns-specific initialization
  var _xpGains = {};
  var _goldGains = 0;
  var _pdGains = 0;
  var _equipmentGains = [];
  if (isCaverns) {
    this.getXpGains = () => {
      return _xpGains;
    };
    this.getGoldGains = () => {
      return _goldGains;
    };
    this.setGoldGains = (n) => {
      _goldGains = n;
    };
    this.setXpGains = (v) => {
      _xpGains = v;
    };
    this.getPDGains = () => {
      return _pdGains;
    };
    this.setPDGains = (n) => {
      _pdGains = n;
    };
    this.getEquipmentGains = () => {
      return _equipmentGains;
    };
    this.setEquipmentGains = (n) => {
      _equipmentGains = n;
    };

    _enemySpawnInterval = new IntervalTimer(spawnEnemy, 5000 + Math.random() * 20000);
  }

  // pause/unpause routines
  let _paused = isCaverns || battleState?.paused || false;
  this.pause = () => {
    _paused = true;
    _battleState.pause();
    _enemySpawnInterval?.pause();
    _last_execution_at = Date.now() - 200; // because DoT ticks might kill a unit, give it a chance to die before executing the next ability
  };
  this.resume = () => {
    _paused = false;
    resumeTime();
  };

  // turn timeout routine
  let _inactivityTimeout;
  function handleInactivityTimeout() {
    try {
      killBattle({
        killed_by: _unitToOrder.team === 'white' ? whitePlayerId : blackPlayerId,
      });
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'handleInactivityTimeout',
        _unitToOrder,
        whitePlayerId,
        blackPlayerId,
      });
    }
  }

  const passTurn = (unit) => {
    if (unit && unit.ap >= unit.max_ap) {
      emitToAllSockets('statCostsExacted', {
        abilityHandle: 'wait',
        actorId: unit.uid,
        victimId: unit.uid,
        apCost: Math.min(unit.ap, unit.max_ap),
        legal: true,
        withinRange: true,
        statsSufficient: true,
      });
      unit.ap = 0;
    }

    blacklistedAbilities.clear();
    _unitToOrder = null;
    this.resume();
  };

  function onFullAP(unit) {
    try {
      if (_unitToOrder) {
        return;
      }
      requestOrders(unit);
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'onFullAP',
        unit,
      });
    }
  }

  this.getUnitToOrder = () => {
    return _unitToOrder;
  };

  function requestOrders(unit) {
    try {
      var blocking = BattleCalc.getCondition(unit, 'blocking');
      if (blocking) {
        _battleState.expireCondition(blocking.uid);
      }

      if (BattleCalc.getCondition(unit, 'stunned')) {
        return;
      }

      var alreadyInQueue = false;
      for (var i = 0; i < _allUnitsToOrder.length; ++i) {
        if (_allUnitsToOrder[i].uid === unit.uid) {
          alreadyInQueue = true;
          break;
        }
      }
      if (!alreadyInQueue) {
        _allUnitsToOrder.push(unit);
      }
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'requestOrders',
        unit,
      });
    }
  }

  function resumeTime() {
    try {
      if (_done) {
        return;
      }

      _lastRequestedIdFromClient = null;

      if (_unitToOrder) {
        _unitToOrder.moveDestination = null;
        _unitToOrder.abilityDestination = null;
        _battleState.allUnits[_unitToOrder.uid].abilityDestination = null;
        _unitToOrder = null;
      }

      _num_orders_requests_this_turn = 0;

      _battleState.resume();

      if (_enemySpawnInterval && !_enemySpawnInterval.force_paused) {
        _enemySpawnInterval.resume();
      }

      emitToAllSockets('timeResumed', {
        allUnits: _battleState.allUnits, // allPieces: _battleState.allPieces
      });
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'resumeTime',
      });
    }
  }
  this.resumeTime = resumeTime;

  let _ability_execution_result_timeout;
  const onAbilityExecutionResult = (result) => {
    emitToAllSockets('abilityExecuted', result);

    // for weapon procs, multi-shot's 1st of 2-phase result, and Angel Mask, don't treat as an executable ability
    if (result.isProc || result.phase === 'fire_arrows' || result.abilityHandle === 'angel_mask')
      return;

    if (result.abilityHandle === 'move') _abilityExecuting = false;
    else
      setTimeout(
        () => {
          _abilityExecuting = false;
        },
        300 // it is very important for this number to be higher than the timeout from DamageEvent to new DeathEvent
      );
  };
  _abilityExecutor.eventEmitter.on('EXECUTION_RESULT', onAbilityExecutionResult);

  const blacklist = (abilityHandle) => {
    try {
      blacklistedAbilities.add(abilityHandle);
      _abilityExecuting = false;
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'blacklist',
        abilityHandle,
      });
    }
  };

  // for attemptExecuteAbility() throttling
  let _last_execution_at = 0;
  let _execution_throttle_timeout;
  let _abilityExecuting = false;
  let _abilityExecutionTimeout;
  function getExecutionRate(ability_handle) {
    switch (ability_handle) {
      case 'move':
        return 200;
      default:
        return 400;
    }
  }
  const attemptExecuteAbility = (this.attemptExecuteAbility = (data, num_recursions = 0) => {
    try {
      if (
        _done ||
        _party_defeated ||
        !data ||
        data === 'wait' ||
        !_abilityExecutor ||
        (_abilityExecuting && !IMMEDIATE_EXECUTION_ABILITIES.includes(data.abilityHandle)) ||
        num_recursions > 100
      ) {
        return;
      }

      // throttle
      const now = Date.now();
      const delta = now - _last_execution_at;
      const execution_rate = getExecutionRate(data.abilityHandle);
      if (delta < execution_rate) {
        clearTimeout(_execution_throttle_timeout);
        _execution_throttle_timeout = setTimeout(() => {
          attemptExecuteAbility(data, num_recursions);
        }, execution_rate - delta);
        return;
      }
      _last_execution_at = now;

      const actor = _battleState.allUnits[data.actorId];
      const victim = _battleState.allPieces[data.victimId];
      if (!actor) {
        return;
      }

      data = _abilityExecutor.attemptIsLegal(data, actor);
      if (!data) {
        return;
      }

      if (data.abilityHandle === 'move') {
        // blacklist the 'move' ability if the path is blocked
        const path = _aStarSearch.getPath(
          _battleState.getTileAtCoords(actor.x, actor.y),
          victim,
          _battleState
        );
        if (path.length < 2 || path[1].occupied) {
          blacklist(data.abilityHandle);
          return;
        }
      }

      if (!data.legal) {
        if (!isCaverns && actor.hero && (fightLive || actor.team === 'white')) {
          const playerId = actor.team === 'white' ? whitePlayerId : blackPlayerId;
          if (playerId) {
            actor.moveDestination = null;
            actor.abilityDestination = null;
            emitToPlayer('illegalAction', { ...data, playerId });
            blacklist(data.abilityHandle);
          }
        } else {
          if (!_battleState.allPieces) {
            return;
          }
          const victim = _battleState?.allPieces[data?.victimId];
          if (!victim?.dead) {
            blacklist(data.abilityHandle);
          }
          return;
        }
      } else {
        actor.abilityDestination = data;

        if (!data.statsSufficient && !isCaverns) {
          const playerId = actor.team === 'white' ? whitePlayerId : blackPlayerId;
          if (playerId) {
            emitToPlayer('insufficientStats', { ...data, playerId });
          }
        } else if (!data.withinRange) {
          if (data.abilityHandle === 'move') {
            actor.moveDestination = victim;
            attemptExecuteAbility(
              _aiHandler.getUnitDecisions()[data.actorId].moveTowards.query(victim),
              ++num_recursions
            );
          } else {
            const dist = BattleCalc.boardDistance(actor, victim);
            const abilityRange = BattleCalc.getEquippedAbility(actor, data.abilityHandle).range;

            if (dist > abilityRange.max) {
              for (const tile of Object.values(_battleState.allTiles || {})) {
                if (
                  BattleCalc.boardDistance(victim, tile) === abilityRange.max &&
                  BattleCalc.pieceIsBetweenPair(tile, [actor, victim])
                ) {
                  actor.moveDestination = tile;
                  attemptExecuteAbility(
                    _aiHandler.getUnitDecisions()[data.actorId].moveTowards.query(victim),
                    ++num_recursions
                  );
                  break;
                }
              }
            } else if (dist < abilityRange.min) {
              var moveAwayResult = _aiHandler
                .getUnitDecisions()
              [data.actorId].moveAway.query(victim);
              if (moveAwayResult && moveAwayResult.victimId) {
                actor.moveDestination = _battleState.allPieces[moveAwayResult.victimId];
                attemptExecuteAbility(moveAwayResult, ++num_recursions);
              } else {
                passTurn(actor);
              }
            }
          }
        } else {
          _abilityExecuting = true;

          data = _abilityExecutor.exactStatCosts(data, _battleState);
          emitToAllSockets('statCostsExacted', data);

          const actor = _battleState.allUnits[data.actorId];
          actor.lastAbilityExecuted = data.abilityHandle;
          const execution_delay = IMMEDIATE_EXECUTION_ABILITIES.includes(data.abilityHandle)
            ? 100
            : BattleCalc.getCooldown(actor) * (isCaverns && !actor.hero ? 150 : 400);

          clearTimeout(_ability_execution_result_timeout);
          clearTimeout(_abilityExecutionTimeout);
          _abilityExecutionTimeout = setTimeout(() => {
            try {
              const status = _abilityExecutor?.execute(data, _battleState);

              if (status?.success === false) _abilityExecuting = false;

              _num_orders_requests_this_turn = 0;
              blacklistedAbilities.clear();

              if (data.abilityHandle === actor.abilityDestination?.abilityHandle)
                actor.abilityDestination = null;
            } catch (err) {
              logError(err, {
                module: 'BattleEngine',
                func: 'attemptExecuteAbility',
                ability_data: JSON.stringify(data),
              });
            }
          }, execution_delay);
        }
      }
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'attemptExecuteAbility',
        ability_data: JSON.stringify(data),
      });
    }
  });

  function onBattleEvent(event) {
    // must be hoisted
    try {
      if (_done) {
        return;
      }

      emitToAllSockets('battleEvent', { event });

      const existing_unit = _battleState.allUnits[event.actorId || event.victimId];
      const unit = existing_unit || event.unit;
      if (unit && !existing_unit) {
        _battleState.allUnits[unit.uid] = unit;
        _battleState.allPieces[unit.uid] = unit;
      }
      if (!unit) {
        _aiHandler.removeUnitDecisions(event.actorId);
        delete _battleState.allUnits[event.actorId];
        delete _battleState.allPieces[event.actorId];
        return;
      }

      if (event.eventHandle === 'unit_spawned') {
        _aiHandler.addUnitDecisions(unit);
        emitToAllSockets('gotUnitState', unit);
      } else if (event.eventHandle === 'move_event' && unit.moveDestination) {
        var moveDest = unit.moveDestination;
        var distToDest = BattleCalc.boardDistance(unit, moveDest);

        if (distToDest === 0 || (moveDest.occupied && distToDest === 1)) {
          unit.moveDestination = null;
          if (unit.abilityDestination) {
            attemptExecuteAbility(unit.abilityDestination);
          }
        } else {
          attemptExecuteAbility(
            _aiHandler.getUnitDecisions()[unit.uid].moveTowards.query(moveDest)
          );
        }
      } else if (event.eventHandle === 'death') {
        _aiHandler.removeUnitDecisions(event.actorId);
        delete _battleState.allUnits[event.actorId];
        delete _battleState.allPieces[event.actorId];

        // if it's the last mob in a caverns room, pause monster spawning a few seconds
        // (giving players a chance to click the unlocked doors/stairs)
        if (isCaverns) {
          const num_enemies = Object.values(_battleState.allUnits).filter(
            ({ team }) => team === 'black'
          ).length;
          if (num_enemies === 0 && _enemySpawnInterval) {
            _enemySpawnInterval.force_paused = true;
            _enemySpawnInterval.pause();
            setTimeout(() => {
              if (_enemySpawnInterval) {
                _enemySpawnInterval.force_paused = false;
                _enemySpawnInterval.resume();
              }
            }, 3000);
          }
        }
      }
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'onBattleEvent',
        event,
      });
    }
  }

  function onUnitCondition({ owner, cState }) {
    // must be hoisted
    try {
      if (_done || _party_defeated) {
        return;
      }

      emitToAllSockets('unitCondition', cState);

      const cHandle = cState.handle;
      if (cHandle === 'inspired' || cHandle === 'armor_broken') {
        updateUnitState(owner.uid, { from_condition: true });
      }
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'onUnitCondition',
        owner,
        cState,
      });
    }
  }

  function onUnitConditionExpired(data) {
    // must be hoisted
    try {
      if (_done || _party_defeated) {
        return;
      }

      emitToAllSockets('unitConditionExpired', data);

      if (data.conditionHandle === 'inspired') {
        updateUnitState(data.owner.uid, { from_condition: true });
      }
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'onUnitConditionExpired',
        data,
      });
    }
  }

  function updateUnitState(unit_uid) {
    // must be hoisted
    const unit = _battleState.allUnits[unit_uid];
    if (_done || !unit) return;

    try {
      const { black_team, white_team } = _battleState;
      const { builds, roster } = unit.team === 'white' ? white_team : black_team;

      const unit_state = unit.ai_unit
        ? getUnitState({ ai_unit: unit })
        : getUnitState({
          unit_build: builds[unit.hero_handle || unit.handle],
          roster_hero: roster[unit.hero_handle || unit.handle],
          unit_conditions: unit.conditions,
        });

      // xfer stats
      unit_state.hp = unit.hp;
      unit_state.ap = unit.ap;
      unit_state.mp = unit.mp;
      if (isNaN(unit_state.hp), isNaN(unit_state.ap), isNaN(unit_state.mp)) {
        console.error('BattleEngine.updateUnitState - NaN stats', { unit_state, unit });
      }
      if (unit_state.hp > unit_state.max_hp) {
        unit_state.hp = unit_state.max_hp;
      }
      if (unit_state.ap > unit_state.max_ap) {
        unit_state.ap = unit_state.max_ap;
      }
      if (unit_state.mp > unit_state.max_mp) {
        unit_state.mp = unit_state.max_mp;
      }

      // Mutate the existing unit in place so AI decision trees (which hold this reference)
      // always see current state. Replacing the object would leave trees with a stale ref
      // and cause duplicate buffs, repeated actions, and units appearing to not act.
      Object.assign(unit, unit_state);
      emitToAllSockets('gotUnitState', unit);
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'updateUnitState',
        unit,
      });
    }
  }

  this.clearTimeouts = () => {
    _abilityExecuting = false;
    clearTimeout(_abilityExecutionTimeout);
  };

  this.remakeDecisionTrees = () => {
    if (_done) {
      return;
    }

    for (const unit of Object.values(_battleState.allUnits || {})) {
      _aiHandler.addUnitDecisions(unit);
    }
  };

  const onBattleResolved = (data) => {
    if (isCaverns) {
      return;
    }

    try {
      if (this.eventEmitter) {
        this.eventEmitter.emit('done', {
          started_at,
          aborted: false,
          battle_id,
          battleState: _battleState,
          blackPlayerId,
          game_mode,
          game_submode,
          whitePlayerId,
          winnerId: data.winner === 'white' ? whitePlayerId : blackPlayerId,
        });
      }
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'onBattleResolved',
        data,
      });
    }
  };
  _battleState.eventEmitter.on(BattleState.BATTLE_RESOLVED, onBattleResolved);

  const killBattle = (this.killBattle = (data) => {
    const { killed_by } = data;
    try {
      if (!this.eventEmitter) {
        logError(new Error('BattleEngine.killBattle called with falsy this.eventEmitter'));
        return;
      }

      this.eventEmitter.emit('done', {
        started_at,
        aborted: true,
        battle_id,
        battleState: _battleState,
        blackPlayerId,
        game_mode,
        game_submode,
        killed_by,
        whitePlayerId,
        winnerId: killed_by === whitePlayerId ? blackPlayerId : whitePlayerId,
      });
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'killBattle',
        killed_by,
      });
    }
  });

  this.dispose = function () {
    try {
      _done = true;

      clearTimeout(_abilityExecutionTimeout);
      clearTimeout(_inactivityTimeout);

      clearInterval(_requestOrdersInterval);
      clearInterval(_angel_mask_interval);

      if (_enemySpawnInterval) {
        _enemySpawnInterval.pause();
        _enemySpawnInterval.dispose();
      }

      if (_abilityExecutor) {
        _abilityExecutor.eventEmitter.removeAllListeners();
        _abilityExecutor.dispose();
      }

      _aiHandler?.dispose();

      if (_battleState) {
        _battleState.eventEmitter.removeAllListeners();
        _battleState.dispose();
      }

      _aStarSearch = null;

      if (this.eventEmitter) {
        this.eventEmitter.removeAllListeners();
        this.eventEmitter = null;
      }
    } catch (err) {
      logError(err, {
        module: 'BattleEngine',
        func: 'dispose',
      });
    }
  };
};
